import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Download, Search, Loader2, ShieldAlert,
  Users, Sparkles, FileText, Mail, Crown, Check, X, Loader,
} from "lucide-react";
import { toast } from "sonner";
import { useSession } from "../lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[var(--accent)]" />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-display text-3xl font-medium text-[var(--ink)]">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { authenticated, user, loading } = useSession();
  const navigate = useNavigate();
  const [forbidden, setForbidden] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState({ users: [], stats: {} });
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [overrideBusy, setOverrideBusy] = useState({});  // user_id -> bool

  const cancelStripe = async (u) => {
    const label = `${u.name || u.email} (${u.email})`;
    const ok = window.confirm(
      `⚠️ Annulation IRRÉVERSIBLE\n\n` +
      `Ceci va :\n` +
      `• Annuler la subscription chez Stripe (plus aucun débit)\n` +
      `• Désactiver Premium en base immédiatement\n` +
      `• Logger l'action dans l'audit\n\n` +
      `Confirmer pour ${label} ?`
    );
    if (!ok) return;
    setOverrideBusy((s) => ({ ...s, [u.user_id]: true }));
    try {
      const { data: r } = await axios.post(
        `${API}/admin/users/${u.user_id}/cancel-stripe`,
        {},
        { timeout: 15000 },
      );
      if (r.stripe_cancelled) {
        toast.success(`✅ ${r.email} annulé chez Stripe + Premium désactivé`);
      } else {
        toast.message(
          `${r.email} : Premium désactivé, mais Stripe: ${r.stripe_error || "non-appelé"}`
        );
      }
      setData((d) => ({
        ...d,
        users: (d.users || []).map((x) =>
          x.user_id === u.user_id
            ? { ...x, is_premium: false, plan: "free",
                subscription_status: "canceled_by_admin", manual_override: true }
            : x
        ),
        stats: {
          ...d.stats,
          premium_users: Math.max(0, (d.stats?.premium_users || 0) - (u.is_premium ? 1 : 0)),
        },
      }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Échec de l'annulation");
    } finally {
      setOverrideBusy((s) => ({ ...s, [u.user_id]: false }));
    }
  };

  const togglePremium = async (u, makePremium) => {
    const reason = window.prompt(
      makePremium
        ? `Activer Premium pour ${u.email} ?\n\nRaison (optionnel — visible dans l'audit) :`
        : `Désactiver Premium pour ${u.email} ?\n\nRaison (optionnel — visible dans l'audit) :`,
      makePremium ? "Override manuel admin" : "",
    );
    if (reason === null) return;  // user cancelled
    setOverrideBusy((s) => ({ ...s, [u.user_id]: true }));
    try {
      const { data: r } = await axios.post(
        `${API}/admin/users/${u.user_id}/premium`,
        { is_premium: makePremium, reason: reason || undefined },
      );
      toast.success(
        makePremium
          ? `Premium activé pour ${r.email}`
          : `Premium désactivé pour ${r.email}`
      );
      // Optimistic local update so the row reflects immediately.
      setData((d) => ({
        ...d,
        users: (d.users || []).map((x) =>
          x.user_id === u.user_id
            ? {
                ...x,
                is_premium: makePremium,
                plan: makePremium ? "premium_manual" : "free",
                subscription_status: makePremium ? "active" : "manually_revoked",
                manual_override: true,
                current_period_end: r.current_period_end || null,
              }
            : x
        ),
        stats: {
          ...d.stats,
          premium_users:
            (d.stats?.premium_users || 0) +
            (makePremium ? 1 : -1) * (u.is_premium === makePremium ? 0 : 1),
        },
      }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Échec de l'action admin");
    } finally {
      setOverrideBusy((s) => ({ ...s, [u.user_id]: false }));
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!authenticated) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      setFetching(true);
      try {
        const { data } = await axios.get(`${API}/admin/users`, {
          params: q ? { q } : undefined,
        });
        setData(data);
        setForbidden(false);
      } catch (e) {
        if (e?.response?.status === 403 || e?.response?.status === 401) {
          setForbidden(true);
        }
      } finally {
        setFetching(false);
      }
    })();
  }, [authenticated, loading, navigate, q]);

  const sortedUsers = useMemo(() => {
    const arr = [...(data.users || [])];
    arr.sort((a, b) => {
      const va = a[sortBy], vb = b[sortBy];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [data.users, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
  };

  if (loading || (fetching && !forbidden)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--ink-dim)]">
        <div className="flex items-center gap-3">
          <Loader2 size={20} className="animate-spin text-[var(--accent)]" />
          <span className="text-sm font-mono">Chargement…</span>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="card p-10 max-w-md text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-[rgba(251,191,36,0.10)] border border-[rgba(251,191,36,0.35)] flex items-center justify-center mb-5">
            <ShieldAlert size={24} className="text-[var(--warning)]" />
          </div>
          <h1 className="font-display text-3xl tracking-tight font-medium">Accès refusé</h1>
          <p className="mt-3 text-sm text-[var(--ink-dim)] leading-relaxed">
            Cette page est réservée aux administrateurs. Vous êtes connecté en
            tant que <span className="font-mono">{user?.email}</span>.
          </p>
          <Link
            to="/"
            data-testid="admin-back-home"
            className="mt-7 btn-ghost inline-flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} /> Retour à l&apos;application
          </Link>
        </div>
      </div>
    );
  }

  const s = data.stats || {};
  const downloadCsv = () => {
    const url = `${API}/admin/users.csv${q ? `?q=${encodeURIComponent(q)}` : ""}`;
    // Open as download — withCredentials triggers cookie auth on same-origin
    window.location.href = url;
  };

  return (
    <div className="min-h-screen relative">
      <div className="bg-mesh" />
      <div className="bg-grid" />

      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[rgba(8,9,12,0.75)] border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-ghost inline-flex items-center gap-2 !py-2 !px-3" data-testid="admin-back-link">
              <ArrowLeft size={14} /> <span className="text-xs">Retour</span>
            </Link>
            <div className="w-px h-6 bg-[var(--border)]" />
            <div>
              <div className="label-eyebrow">Admin · SmartResume</div>
              <h1 className="font-display text-xl font-medium leading-tight mt-1">Utilisateurs</h1>
            </div>
          </div>
          <button
            onClick={downloadCsv}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-bright)] bg-[var(--surface)] hover:bg-[var(--surface-2)] text-[12px] text-[var(--ink)] tracking-wide transition"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="Comptes" value={s.total_users ?? 0} />
          <StatCard icon={Crown} label="Premium" value={s.premium_users ?? 0} />
          <StatCard icon={Sparkles} label="Actifs" value={s.active_users ?? 0} />
          <StatCard icon={FileText} label="CV générés" value={s.total_cvs ?? 0} />
          <StatCard icon={Mail} label="Lettres" value={s.total_cover_letters ?? 0} />
        </div>

        {/* Search */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-dim)]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par email ou nom…"
              data-testid="admin-search"
              className="w-full pl-9 pr-3 py-2 rounded-md bg-[var(--bg-2)] border border-[var(--border)] text-[var(--ink)] placeholder:text-[var(--muted)] text-sm focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
          <div className="text-[11px] text-[var(--ink-dim)] font-mono">
            {sortedUsers.length} résultat{sortedUsers.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-dim)] border-b border-[var(--border)]">
                  <Th onClick={() => toggleSort("email")} active={sortBy === "email"} dir={sortDir}>Utilisateur</Th>
                  <Th onClick={() => toggleSort("is_premium")} active={sortBy === "is_premium"} dir={sortDir}>Plan</Th>
                  <Th onClick={() => toggleSort("doc_count")} active={sortBy === "doc_count"} dir={sortDir} align="right">Docs</Th>
                  <Th onClick={() => toggleSort("cv_count")} active={sortBy === "cv_count"} dir={sortDir} align="right">CV</Th>
                  <Th onClick={() => toggleSort("cover_letter_count")} active={sortBy === "cover_letter_count"} dir={sortDir} align="right">Lettres</Th>
                  <Th onClick={() => toggleSort("created_at")} active={sortBy === "created_at"} dir={sortDir}>Inscription</Th>
                  <Th onClick={() => toggleSort("last_login_at")} active={sortBy === "last_login_at"} dir={sortDir}>Dernier login</Th>
                  <th className="px-4 py-3 text-right text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-dim)]">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--ink-dim)] italic">Aucun utilisateur.</td></tr>
                )}
                {sortedUsers.map((u) => (
                  <tr key={u.user_id} className="border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--surface-2)]/40 transition" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-[var(--border-bright)]" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[var(--surface-2)] border border-[var(--border-bright)] flex items-center justify-center text-[11px] font-semibold">
                            {(u.name || u.email).slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-[var(--ink)] truncate">{u.name || "—"}</div>
                          <div className="text-[11px] text-[var(--ink-dim)] font-mono truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      {!u.is_premium ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-[var(--border-bright)] bg-[var(--bg-2)] text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-dim)]">Gratuit</span>
                      ) : (
                        <span
                          title={u.manual_override ? "Override admin manuel" : u.plan}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10.5px] font-mono uppercase tracking-wider"
                          style={{
                            background: "linear-gradient(135deg, rgba(125,211,252,0.15), rgba(167,139,250,0.15))",
                            border: "1px solid rgba(125,211,252,0.4)",
                            color: "#7dd3fc",
                          }}
                        >
                          <Crown size={10} /> {u.manual_override ? "Manuel" : (u.plan || "premium")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums">{u.doc_count}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-[var(--ink-dim)]">{u.cv_count}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-[var(--ink-dim)]">{u.cover_letter_count}</td>
                    <td className="px-4 py-3.5 text-[12px] text-[var(--ink-dim)] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3.5 text-[12px] text-[var(--ink-dim)] whitespace-nowrap">{fmtDate(u.last_login_at)}</td>
                    <td className="px-4 py-3.5 text-right">
                      {overrideBusy[u.user_id] ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-[var(--ink-dim)] font-mono">
                          <Loader size={12} className="animate-spin" /> …
                        </span>
                      ) : u.is_premium ? (
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          <button
                            onClick={() => togglePremium(u, false)}
                            data-testid={`admin-revoke-premium-${u.user_id}`}
                            title="Révoquer Premium en base (n'annule PAS Stripe)"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[var(--border-bright)] bg-[var(--surface-2)] hover:bg-[rgba(239,68,68,0.10)] hover:border-[rgba(239,68,68,0.40)] hover:text-[#fca5a5] text-[11px] font-mono tracking-wide text-[var(--ink-dim)] transition"
                          >
                            <X size={11} /> Désactiver
                          </button>
                          <button
                            onClick={() => cancelStripe(u)}
                            data-testid={`admin-cancel-stripe-${u.user_id}`}
                            title="Annule chez Stripe (plus aucun débit) ET désactive Premium"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[11px] font-mono tracking-wide transition"
                            style={{
                              background: "rgba(239,68,68,0.10)",
                              borderColor: "rgba(239,68,68,0.40)",
                              color: "#fca5a5",
                            }}
                          >
                            <X size={11} /> Annuler Stripe
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => togglePremium(u, true)}
                          data-testid={`admin-grant-premium-${u.user_id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-mono tracking-wide transition"
                          style={{
                            background: "linear-gradient(135deg, rgba(125,211,252,0.12), rgba(167,139,250,0.12))",
                            border: "1px solid rgba(125,211,252,0.40)",
                            color: "#7dd3fc",
                          }}
                        >
                          <Check size={11} /> Activer Premium
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function Th({ children, onClick, active, dir, align = "left" }) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-3 cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"} ${active ? "text-[var(--ink)]" : "hover:text-[var(--ink)]"}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && <span className="text-[var(--accent)]">{dir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
}
